import { TradingType, WalletType } from '../customers/types';
import { IGroup } from '../groups/types';
import { IChallengeLimit, IChallengeObjective } from '../propTrading/types';

export const currencies = [{ label: 'USD', value: 'USD' }];

export const types = [
  { label: 'Demo', value: 'DEMO' },
  { label: 'Real', value: 'REAL' },
];

export const tradingTypesOptions = [
  { label: 'Hedging', value: TradingType.Hedging },
  { label: 'Netting', value: TradingType.Netting },
];

export const typesOptions = [
  { label: 'TRADING', value: WalletType.Trading },
  { label: 'PROP_CHALLENGE', value: WalletType.PropChallenge },
  { label: 'PROP_FUNDED', value: WalletType.PropFunded },
  { label: 'AFFILIATE', value: WalletType.Affiliate },
];

export enum WalletStatus {
  Active = 'ACTIVE',
  Disabled = 'DISABLED',
}

export enum WalletPlatform {
  Wintrado = 'WINTRADO',
  MT5 = 'MT5',
}

/**
 * MT5 accounts have no Wintrado gateway account, so no balances, positions, credit or transfers.
 * Stated positively so a platform added later is treated as having no gateway account until someone
 * says otherwise, rather than silently inheriting balance and transfer behaviour it cannot support.
 */
export const isGatewayWallet = (wallet: Pick<IWallet, 'platform'>): boolean =>
  wallet.platform === WalletPlatform.Wintrado;

export enum PropChallengeAccountStatus {
  Active = 'ACTIVE',
  Failed = 'FAILED',
  Passed = 'PASSED',
}
export interface IWallet {
  id: number;
  type: string;
  currency: string;
  status: WalletStatus;
  platform: WalletPlatform;
  primary: boolean;
  group: IGroup;
  leverage: number;
  customer: {
    name: string;
    id: string;
  };
  isDemo: boolean;
  tradingType: TradingType;
  propFunnelId: string | null;
  createdAt: string;
}

export interface IWalletWithBalance extends IWallet {
  equity: string;
  margin: string;
  marginFree: string;
  profitLoss: string;
  marginLevelPercent: number;
  balance: string;
  credit: string;
}

export interface IWalletWithBalanceAndStage extends IWalletWithBalance {
  currentStage: number;
  maxStage: number;
}

export interface IFundedAccountWithBalance extends IWalletWithBalanceAndStage {
  fundedAccountTypeId: number;
  behavior: string;
  availableWithdrawalFunds: string;
  traderProfitShareFactor: string;
}

export interface IPropChallengeAccount extends IWalletWithBalanceAndStage {
  propChallengeId: number;
  propStatus: PropChallengeAccountStatus;
  startedAt: Date;
  endedAt: Date | null;
}

export interface IWalletPerformanceRecord {
  day: string;
  balance: string | null;
  credit: string | null;
  equity: string | null;
}

export interface IWalletQuery {
  /**
   * Balances are present for every platform — MT5's come from the bridge rather than the gateway.
   * `history` is the part MT5 cannot supply: daily snapshots are a gateway concept.
   */
  wallet: IWalletWithBalance | IFundedAccountWithBalance | IPropChallengeAccount;
  history: Array<IWalletPerformanceRecord>;
  limits?: Array<IChallengeLimit>;
  objectives?: Array<IChallengeObjective>;
}

export interface IWalletsQuery {
  pages: number;
  /** Total matching the filters, not the page — drives the platform tab counts. */
  total: number;
  wallets: Array<IWalletWithBalance>;
}

/**
 * Same shape as {@link IWalletsQuery}. Kept as its own name because the two lists are paginated
 * and sorted by different machinery — this one in Postgres, the Wintrado one by the gateway.
 */
export type IMt5WalletsQuery = IWalletsQuery;

export interface IMt5Settings {
  mt5Enabled: boolean;
  server: { name: string; address: string };
  downloadLinks: { desktop?: string; android?: string; ios?: string };
}

export interface IRoutingAdapter {
  id: number;
  name: string;
}
